export type AppVersionRerunSeverity = 'none' | 'recommended' | 'required';

export interface AppVersionPolicy {
  currentVersion: string;
  // Optional app-specific overrides. These are useful when a known bug or data
  // migration affects only a specific cutoff version instead of following the
  // generic major/minor/patch rule below.
  rerunRequiredBefore?: string;
  rerunRecommendedBefore?: string;
  message?: string;
}

export interface AppVersionEvaluation {
  severity: AppVersionRerunSeverity;
  savedVersion?: string;
  currentVersion: string;
  threshold?: string;
  message: string;
}

function parseVersion(version: string): number[] {
  // Accept simple semver-like values such as "1.2.3" and tolerate suffixes such
  // as "1.2.3-beta" by reading the numeric prefix of each segment.
  return version
    .trim()
    .split('.')
    .map(part => {
      const match = part.match(/^\d+/);
      return match ? Number(match[0]) : 0;
    });
}

export function compareVersions(left: string, right: string): number {
  // Numeric segment comparison with missing segments treated as zero:
  // 1.1 == 1.1.0, 1.1.1 > 1.1.0, 2.0.0 > 1.9.9.
  const leftParts = parseVersion(left);
  const rightParts = parseVersion(right);
  const length = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < length; index += 1) {
    const leftPart = leftParts[index] ?? 0;
    const rightPart = rightParts[index] ?? 0;

    if (leftPart < rightPart) return -1;
    if (leftPart > rightPart) return 1;
  }

  return 0;
}

function getImplicitVersionSeverity(savedVersion: string, currentVersion: string): AppVersionRerunSeverity {
  // If the saved record is already at or ahead of the current app version,
  // CalcPad should not flag it as stale.
  if (compareVersions(savedVersion, currentVersion) >= 0) {
    return 'none';
  }

  const savedParts = parseVersion(savedVersion);
  const currentParts = parseVersion(currentVersion);

  // Version shape: major.minor.patch.
  // Major/minor bumps require rerun; patch bumps recommend rerun.
  if ((savedParts[0] ?? 0) !== (currentParts[0] ?? 0) || (savedParts[1] ?? 0) !== (currentParts[1] ?? 0)) {
    return 'required';
  }

  if ((savedParts[2] ?? 0) !== (currentParts[2] ?? 0)) {
    return 'recommended';
  }

  return 'none';
}

export function evaluateAppVersion(savedVersion: string | null | undefined, policy: AppVersionPolicy): AppVersionEvaluation {
  // Evaluation order is intentional:
  // 1. Missing versions are suspicious but not automatically invalid.
  // 2. Explicit policy cutoffs override generic version semantics.
  // 3. Otherwise use the implicit major/minor/patch rule.
  if (!savedVersion) {
    return {
      severity: 'recommended',
      currentVersion: policy.currentVersion,
      message: 'The saved app version is unavailable. Re-run this calculation before relying on the stored result.'
    };
  }

  if (policy.rerunRequiredBefore && compareVersions(savedVersion, policy.rerunRequiredBefore) < 0) {
    return {
      severity: 'required',
      savedVersion,
      currentVersion: policy.currentVersion,
      threshold: policy.rerunRequiredBefore,
      message: policy.message || 'A calculation bug was fixed after this run was saved. Re-run before relying on the stored result.'
    };
  }

  if (policy.rerunRecommendedBefore && compareVersions(savedVersion, policy.rerunRecommendedBefore) < 0) {
    return {
      severity: 'recommended',
      savedVersion,
      currentVersion: policy.currentVersion,
      threshold: policy.rerunRecommendedBefore,
      message: policy.message || 'This run was saved with an older app version. Re-run is recommended before relying on the stored result.'
    };
  }

  const implicitSeverity = getImplicitVersionSeverity(savedVersion, policy.currentVersion);
  if (implicitSeverity === 'required') {
    return {
      severity: 'required',
      savedVersion,
      currentVersion: policy.currentVersion,
      threshold: policy.currentVersion,
      message: policy.message || 'This run was saved with a calculation-version change. Re-run before relying on the stored result.'
    };
  }

  if (implicitSeverity === 'recommended') {
    return {
      severity: 'recommended',
      savedVersion,
      currentVersion: policy.currentVersion,
      threshold: policy.currentVersion,
      message: policy.message || 'This run was saved with an older patch app version. Re-run is recommended before relying on the stored result.'
    };
  }

  return {
    severity: 'none',
    savedVersion,
    currentVersion: policy.currentVersion,
    message: ''
  };
}

export function formatVersionWarning(appName: string, evaluation: AppVersionEvaluation): string {
  const savedVersionText = evaluation.savedVersion ?? 'unknown version';
  return `${appName} was saved with ${savedVersionText}. Current version is ${evaluation.currentVersion}. ${evaluation.message}`;
}
